import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { starTask, unstarTask } from 'api/tasks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Task } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { PaginatedData } from 'types/pagination';

const useStarTask = (task: Task) => {
    const { accountId } = useCurrentAccount();
    const { error } = useToast();

    return useMutation<unknown, GivebutterAxiosError, { star: boolean }, { originalTask: Task }>({
        mutationFn: ({ star }) =>
            star ? starTask(accountId, task.id) : unstarTask(accountId, task.id),
        onMutate: ({ star }) => {
            queryClient.setQueryData(
                accountQueryKeys.tasks(accountId),
                (oldData: PaginatedData<Task>) => {
                    if (oldData?.data?.find(t => t.id === task.id)) {
                        const newTasks = oldData.data.map(oldTask => {
                            if (oldTask.id !== task.id) return oldTask;
                            return {
                                ...oldTask,
                                is_starred: star,
                            };
                        });

                        return {
                            ...oldData,
                            data: newTasks,
                        };
                    }
                },
            );

            return { originalTask: task };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tasks(accountId) });
        },
        onError: (e, _, context) => {
            error(e.response?.data.message || 'Unable to star Task. Please try again');

            if (context?.originalTask) {
                queryClient.setQueryData(
                    accountQueryKeys.taskById(accountId, task.id),
                    () => context.originalTask,
                );
            }
        },
    });
};

export { useStarTask };
